/**
 * Material-to-color map. Each material in a project's materials list resolves
 * to a representative color swatch (primary + accent shadow). Used by
 * ProjectScrollNarrative to render visual swatches alongside the names.
 *
 * Map is keyword-matched (case-insensitive substring) so partial names like
 * "Quarter-sawn white oak" still hit "oak".
 */

interface Swatch {
  base: string;
  shadow: string;
}

const MATERIAL_SWATCHES: Array<{ keywords: RegExp; swatch: Swatch }> = [
  // Plaster + walls
  { keywords: /lime\s*wash|limewash/i, swatch: { base: '#e8ddc6', shadow: '#c8b89a' } },
  { keywords: /plaster/i, swatch: { base: '#d8c8b0', shadow: '#a89878' } },

  // Metals
  { keywords: /weathered\s*bronze|aged\s*bronze|bronze/i, swatch: { base: '#704d28', shadow: '#3a2410' } },
  { keywords: /antique\s*brass|brass/i, swatch: { base: '#a87444', shadow: '#5a3a22' } },
  { keywords: /blackened\s*steel|steel/i, swatch: { base: '#1a1410', shadow: '#0a0805' } },

  // Wood
  { keywords: /quarter[-\s]?sawn\s*(white\s*)?oak|white\s*oak/i, swatch: { base: '#c89a64', shadow: '#7a5230' } },
  { keywords: /quarter[-\s]?sawn\s*ash|ash/i, swatch: { base: '#d4b890', shadow: '#8a6440' } },
  { keywords: /heart[-\s]?pine|pine/i, swatch: { base: '#c89a64', shadow: '#7a5230' } },
  { keywords: /walnut/i, swatch: { base: '#3d2818', shadow: '#1a0e06' } },
  { keywords: /teak/i, swatch: { base: '#a87444', shadow: '#5a3a22' } },

  // Stone
  { keywords: /vermont\s*marble|carrara|marble/i, swatch: { base: '#e8e0d0', shadow: '#a89878' } },

  // Textiles / soft
  { keywords: /bouclé|boucle|linen/i, swatch: { base: '#d8c8b0', shadow: '#a89878' } },
  { keywords: /mohair|oxblood/i, swatch: { base: '#5a1a1a', shadow: '#2a0a08' } },
  { keywords: /sisal/i, swatch: { base: '#a89878', shadow: '#6a5a3a' } },
  { keywords: /linen[-\s]?wrapped|cane|rattan/i, swatch: { base: '#c8a878', shadow: '#8a6440' } },
  { keywords: /lacquer/i, swatch: { base: '#0d0905', shadow: '#000000' } },
  { keywords: /cork/i, swatch: { base: '#a87444', shadow: '#5a3a22' } },

  // Glass / etc.
  { keywords: /low[-\s]?iron\s*glass|glass/i, swatch: { base: '#c8d8e8', shadow: '#7a8a9a' } },
];

export function getMaterialSwatch(material: string): Swatch {
  for (const entry of MATERIAL_SWATCHES) {
    if (entry.keywords.test(material)) return entry.swatch;
  }
  // Default neutral fallback
  return { base: '#a89878', shadow: '#5a4a3a' };
}
